import { useCallback, useMemo, useState } from 'react';
import SemblePhysioBookings from '../services/SemblePhysioBookings';
import errorActions from '../actions/errorActions';
import useAuthentication from './useAuthentication';

const useSemblePhysioBookings = () => {
  const { refresh, access_token: accessToken } = useAuthentication();
  const { log: logError } = errorActions();

  const physioBookings = useMemo(
    () => SemblePhysioBookings.getInstance(accessToken, refresh, logError),
    [accessToken],
  );

  const [appointments, setAppointments] = useState(null);
  const [loading, setLoading] = useState(false);
  const [appointmentCancelled, setAppointmentCancelled] = useState(false);
  const [sembleId, setSembleId] = useState(null);

  const getAppointments = useCallback(
    async idaasId => {
      setLoading(true);
      try {
        const { sembleId: resolvedId } = await physioBookings.getSembleId({ idaasId });
        setSembleId(resolvedId);
        const data = await physioBookings.getAppointments({ sembleId: resolvedId });
        setAppointments(data);
      } catch (e) {
        // Error is already surfaced via the shared GenericError modal by the service's logError call.
      } finally {
        setLoading(false);
      }
    },
    [physioBookings],
  );

  const cancelAppointment = useCallback(
    async appointmentId => {
      try {
        await physioBookings.cancelAppointment({ appointmentId, patientId: sembleId });
        setAppointments(prev =>
          prev?.map(appt =>
            appt['appointment-id'] === appointmentId
              ? { ...appt, 'appointment-status': 'cancelled' }
              : appt,
          ),
        );
        setAppointmentCancelled(true);
      } catch (e) {
        // Error is already surfaced via the shared GenericError modal by the service's logError call.
      }
    },
    [physioBookings, sembleId],
  );

  const resetCancelled = useCallback(() => setAppointmentCancelled(false), []);

  return { appointments, loading, appointmentCancelled, resetCancelled, getAppointments, cancelAppointment };
};

export default useSemblePhysioBookings;
